import { toastError } from '@onestep/coreui/Toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import moment from 'moment';
import { useEffect } from 'react';

import { invalidatePatientOverviewData } from '@/services/patientOverviewData';

import useMixpanel from '../../../hooks/useMixpanel';
import useStores from '../../../hooks/useStores';
import type { IClinicalEvent, IClinicalVersion, IUseRtmEventsService } from './types';
import { rtmEventsAllowedActions } from './types';

export default function useRtmEventsService(patientUuid: string): IUseRtmEventsService {
  const queryClient = useQueryClient();
  const { track } = useMixpanel();
  const {
    patientsServiceStore: { queryKey },
  } = useStores();
  const result = useQuery<{ result: IClinicalEvent[] }>({
    queryKey: [
      'reported_events',
      'event',
      'list',
      {
        patientUuid,
      },
    ],
    retry: false,
    enabled: !!patientUuid,
  });

  const insertAction = useMutation({
    mutationFn: async ({
      serviceTime,
      localeTimezone,
    }: {
      serviceTime: string;
      localeTimezone: string;
    }) => {
      const result = await axios.post<IClinicalEvent>('/api/reported_events/event/', {
        patientUuid,
        serviceTime,
        localeTimezone,
      });
      return result?.data?.uuid;
    },
  });

  const voidAction = useMutation({
    mutationFn: async ({ uuid }: { uuid: string; isMonitoring?: boolean }) => {
      await axios.delete(`/api/reported_events/event/${uuid}/`);
      result.refetch();
    },
    onSuccess: (_, { isMonitoring = false }) => {
      setTimeout(async () => {
        await invalidatePatientOverviewData(queryClient, patientUuid);
        if (isMonitoring) {
          invalidatePatientsQuery();
        }
      }, 250);
    },
  });

  const amendAction = useMutation({
    mutationFn: async ({ data }: { data: IClinicalVersion; isMonitoring?: boolean }) => {
      await axios.put(`/api/reported_events/event/${data.uuid}/versions/`, {
        ...data,
      });
      result.refetch();
    },
    onSuccess: (_, { isMonitoring = false }) => {
      setTimeout(async () => {
        await invalidatePatientOverviewData(queryClient, patientUuid);
        if (isMonitoring) {
          invalidatePatientsQuery();
        }
      }, 250);
    },
  });

  const invalidatePatientsQuery = async () => {
    await queryClient.invalidateQueries({ queryKey });
  };

  const events = result?.data?.result ?? [];

  const isEventInEditableDateRange = (serviceTime: string) =>
    moment().local().isBefore(moment(serviceTime).add(30, 'days').local());

  const isActionAllowd = (event: IClinicalEvent, action: rtmEventsAllowedActions) =>
    event?.allowedActions?.some((x) => x === action || x === '*');

  useEffect(() => {
    if (result?.isError) {
      toastError({ content: 'Failed to get reported events list' });
      track('failed_get_reported_events_list', {
        error: result?.error,
      });
    }
  }, [result?.isError]);

  return {
    data: events,
    isLoading: result?.isLoading ?? false,
    getEvent: (uuid: string) => events?.find((x) => x.uuid === uuid),
    insertAction: insertAction.mutateAsync,
    allowAmmend(uuid: string) {
      const event = events.find((x) => x.uuid === uuid);
      return (
        (event &&
          isEventInEditableDateRange(event.serviceTime) &&
          isActionAllowd(event, rtmEventsAllowedActions.write)) ??
        false
      );
    },
    ammendAction: amendAction.mutateAsync,
    allowVoid(uuid: string) {
      const event = events.find((x) => x.uuid === uuid);
      return (
        (event &&
          isEventInEditableDateRange(event.serviceTime) &&
          isActionAllowd(event, rtmEventsAllowedActions.write) &&
          !event.isVoid) ??
        false
      );
    },
    voidAction: voidAction.mutateAsync,
    isEventInEditableDateRange(uuid: string) {
      const event = events.find((x) => x.uuid === uuid);
      return (event && isEventInEditableDateRange(event.serviceTime)) ?? false;
    },
    isError: result?.isError ?? false,
  };
}
